/**
 * Agent Eval Service
 *
 * API client for the real-time audio gateway that streams
 * browser audio to EC2 Whisper/Pyannote services and forwards
 * transcript chunks to the Flow Executor for slot extraction
 * and flow advancement.
 */

import axios from 'axios'
import { environment } from '../environments/environment'

// Types

/**
 * Flow/intent/extraction context the gateway records on a patient segment
 * at processing time (DM-7768). Absent on navigator rows, on sessions
 * recorded before the feature, and on turns the executor never processed.
 */
export interface SegmentAnnotation {
  // 'answer' = answered the current question; 'flow_trigger' = this
  // utterance itself started the flow; the rest are non-extraction turns.
  turn_kind?: 'answer' | 'flow_trigger' | 'inbound_unrouted' | 'post_flow_ack' | 'urgency_override' | 'executor_error'
  flow_name?: string
  flow_step?: string
  slot_name?: string // The slot the question was trying to fill
  slot_type?: string
  question_description?: string // Step description from the flow spec
  question_template?: string // Raw utter_ask (uninterpolated)
  extracted_slots?: Record<string, any> // Raw LLM extraction this turn
  normalized_slots?: Record<string, any> // Post-normalization deltas
  branch_taken?: string
  orchestration_action?: string
  flow_started_this_turn?: boolean
}

export interface TranscriptSegment {
  speaker: string
  start: number
  end: number
  text: string
  timestamp?: string
  // 'agent_suggestion' for entries dispatched from the agent_response SSE
  // event (what the agent recommends the provider/navigator should say).
  // Default/undefined means 'spoken' — either Whisper transcription of
  // the navigator's voice or text submitted via "Use as clinician".
  source?: 'spoken' | 'agent_suggestion'
  // Transcript review (DM-7768) — present on segments fetched from the
  // gateway (/summary), not on live SSE rows.
  annotation?: SegmentAnnotation | null
  grade_capture_correct?: boolean | null
  grade_corrected_value?: string | null
  grade_notes?: string | null
}

/**
 * UI Action specification for database operations.
 * Represents a proposed action that maps to a backend API call.
 */
export interface UIAction {
  type: string // medication_status_update, questionnaire_answer, medication_titrate, etc.
  display_text: string // Human-readable description shown to navigator
  description: string // Longer explanation (shown on expand/hover)
  api_call: {
    method: string // HTTP method (GET, POST, PUT, DELETE)
    endpoint: string // API path with {placeholders}
    path_params?: Record<string, any> // URL path substitutions
    body?: Record<string, any> // Request body
  }
  context?: Record<string, any> // Additional display data (medication name, dose, etc.)
}

export type ProposalType = 'conversational' | 'ui_action' | 'combined'
export type ProposalDecision = 'approved' | 'rejected' | 'deferred'
export type ExecutionStatus = 'pending' | 'success' | 'failed'

/**
 * LLM-captured slot value from the executor's active flow.
 * Shown to providers so they can verify/correct what the agent "heard".
 */
export interface SlotFill {
  slot_name: string // e.g., "bp_med_taking_exact"
  display_name: string // e.g., "Taking Exact"
  value: any // The captured value
  source: 'llm_inferred' | 'user_corrected'
  slot_type?: 'bool' | 'categorical' | 'text' | 'float' | 'list' | 'any'
  allowed_values?: string[] // For categorical slots: ["yes", "no"], ["stopped taking it", ...]
}

/**
 * Agent proposal that can include conversational responses and/or UI actions.
 *
 * - conversational: response_text only (what to say)
 * - ui_action: action only (what to do)
 * - combined: both response_text and action (say X AND do Y)
 */
export interface AgentProposal {
  id: string
  proposal_type: ProposalType
  timestamp: number
  flow_name: string
  flow_id?: string // Machine-readable flow name (e.g., "confirm_medication_status")
  flow_step?: string // Current step in the flow
  flow_description?: string
  triggered_by: string // transcript segment that triggered it

  // Conversational component
  response_text?: string

  // UI Action component
  action?: UIAction

  // Slot fills — LLM-captured values from the active flow
  slot_fills?: SlotFill[]

  // Decision tracking
  decision?: ProposalDecision
  decision_timestamp?: string

  // Execution tracking (for ui_action proposals)
  execution_status?: ExecutionStatus
  execution_error?: string

  // Phrasing — original template vs LLM-generated
  original_template?: string

  // Legacy fields
  slots?: Record<string, any>
  text?: string // Backward compatibility
}

/**
 * Flow context from the executor — shows which flow is currently active.
 */
export interface FlowContext {
  flow_name: string // Machine-readable (e.g., "confirm_medication_status")
  flow_step: string | null // Current step ID
  display_name: string // Human-readable (e.g., "Confirm Medication Status")

  // Orchestrator context — present when active flow is a sub-flow of an orchestrator
  parent_flow?: string // e.g., "check_in"
  parent_display_name?: string // e.g., "Check In"
  parent_step?: string // e.g., "call_update_encounters"
  step_number?: number | null // 1-based position in orchestrator sequence
  total_steps?: number // Total sub-flow calls in orchestrator

  // LLM-captured slot values for the active flow
  slot_fills?: SlotFill[]
}

/**
 * Post-encounter summary with proposals, decisions, and stats.
 */
export interface SessionSummary {
  session_id: string
  patient_id: string | null
  start_time: string
  end_time: string | null
  encounter_type: string | null
  flows_triggered: string[]
  dismissed_flows: Array<{ flow_name: string; reason: string }>
  proposals: Array<AgentProposal & {
    grade_flow_correct?: boolean | null
    grade_capture_correct?: boolean | null
    grade_notes?: string | null
    grade_phrasing_quality?: number | null
    grade_phrasing_edit?: string | null
  }>
  // Annotated transcript (DM-7768). Optional: absent when talking to a
  // gateway older than the transcript-review feature.
  transcript_segments?: TranscriptSegment[]
  encounter_grade?: {
    overall_quality?: number | null
    notes?: string | null
  } | null
  stats: {
    total_proposals: number
    approved: number
    rejected: number
    deferred: number
    phrasing_graded?: number
    phrasing_avg?: number | null
    phrasing_edits_provided?: number
    segments_graded?: number
  }
}

export interface FlowTask {
  task_id: number
  flow_name: string
  title: string
  display_order: number
  status: 'completed' | 'running' | 'queued'
  task_status?: string
  optional?: boolean
  owner_role?: string
  is_adhoc?: boolean
  adhoc_origin?: AdhocOrigin
}

export type AdhocOrigin = 'cross_stage' | 'patient_triggered' | 'provider_initiated' | 'system_generated'

export interface AdhocFlowStartedEvent {
  flow_name: string
  task_id: number
  adhoc_origin: AdhocOrigin
  title: string
  task_stage?: string | null    // Display name of the task's originating stage (cross-stage)
  patient_stage?: string | null // Display name of the patient's current stage (cross-stage)
}

export interface AdhocFlowCompletedEvent {
  flow_name: string
  task_id: number
  adhoc_origin: AdhocOrigin
  started_at: string
  completed_at: string
  proposal_count: number
}

export interface FlowTriggerRequest {
  flow_name: string
  create_task?: boolean
  adhoc_origin?: string
  data?: Record<string, any>
}

export interface FlowTriggerResponse {
  status: string
  flow_name: string
  task_id?: number | null
  adhoc_origin?: string | null
  agent_says?: string // Initial prompt from the flow's first observation step (executor mode)
  flow_context?: FlowContext | null
}

export interface AdhocFlowRegistryEntry {
  flow_name: string
  display_name: string
  category: string
  description: string
  user_role: string
}

export interface SessionStartRequest {
  patient_id: string
  patient_name?: string
  encounter_type?: string
  advance_mode?: 'auto' | 'hybrid' | 'manual'
}

export interface SessionStartResponse {
  session_id: string
  websocket_url: string
  events_url: string
  advance_mode?: string
  flow_task_queue?: FlowTask[]
  flow_task_count?: number
}

export interface SessionStatusResponse {
  session_id: string
  status: 'active' | 'ended' | 'error'
  started_at: string
  transcript_segments: TranscriptSegment[]
  proposals: AgentProposal[]
  error?: string
}

export interface TranscriptCorrection {
  old_speaker: string
  new_speaker: string
  text: string
}

export interface FlowCompletedEvent {
  completed_task: FlowTask | null
  next_task: FlowTask | null
  remaining_count: number
  completed_count: number
  status: 'paused'
}

export interface TaskProgressEvent {
  current_task: FlowTask
  remaining_count: number
  completed_count: number
  total_count: number
}

export interface EncounterFlowsCompleteEvent {
  completed_count: number
  completed_tasks: Array<{ task_id: number; flow_name: string; title: string }>
}

export interface AgentResponseEvent {
  flow_name: string
  flow_step?: string | null
  text: string
  waiting_for?: string
  is_initial_prompt?: boolean
}

export interface SSEEvent {
  type: 'transcript' | 'proposal' | 'status' | 'error' | 'transcript_correction' | 'flow_context' | 'flow_dismissed' | 'slot_corrected' | 'flow_completed' | 'task_progress' | 'encounter_flows_complete' | 'flow_stopped' | 'task_completed' | 'action_executed' | 'adhoc_flow_started' | 'adhoc_flow_completed' | 'unplanned_flow' | 'agent_response'
  data: TranscriptSegment | AgentProposal | FlowContext | { status: string } | { error: string } | TranscriptCorrection | { dismissed_flow: string; display_name: string; reason: string } | { slot_name: string; new_value: any; source: string } | FlowCompletedEvent | TaskProgressEvent | EncounterFlowsCompleteEvent | AdhocFlowStartedEvent | AdhocFlowCompletedEvent | AgentResponseEvent
  timestamp: number
}

// Gateway endpoint — the gateway is now a dedicated service on its own
// subdomain (configured per-environment). See environment.gateway.endpoint.
const getGatewayEndpoint = (): string => {
  return environment.gateway.endpoint
}

/**
 * IdP access token for the gateway.
 *
 * The gateway (FastAPI) validates the SAME Okta token the rest of the app
 * sends to the Flask backend (via `atman.flask.okta_auth` introspection) —
 * REST clients send `Authorization: Bearer <token>`, WS/SSE clients pass
 * `?token=<token>` (they cannot set headers).
 *
 * Token source mirrors the Axios request interceptor (httpInterceptor.tsx):
 *   - Okta mode: the backend-minted JWE (`sessionStorage.encryptedToken`,
 *     populated by /auth/token-verification on any backend call and
 *     refreshed every 15 min). The gateway JWE-unwraps it exactly like
 *     okta_verifier does.
 *   - OIDC (Azure) mode: `localStorage.access_token`. (AIDA-only — AIDA
 *     does not use the gateway, kept as a fallback for dev parity.)
 *
 * Both sources are readable synchronously, so the WS/SSE URL builders can
 * stay synchronous.
 */
const getGatewayToken = (): string => {
  if (environment?.okta?.isEnabled) {
    return window.sessionStorage.getItem('encryptedToken') || ''
  }
  return localStorage.getItem('access_token') || ''
}

class AgentEvalService {
  private gatewayUrl: string

  constructor() {
    this.gatewayUrl = getGatewayEndpoint()
  }

  /**
   * Headers for authenticated REST calls to the gateway.
   * Merges in the IdP bearer token alongside any caller-provided headers.
   */
  private authHeaders(extra: Record<string, string> = {}): Record<string, string> {
    return {
      Authorization: `Bearer ${getGatewayToken()}`,
      ...extra,
    }
  }

  /**
   * Start a new evaluation session
   */
  async startSession(request: SessionStartRequest): Promise<SessionStartResponse> {
    const response = await fetch(`${this.gatewayUrl}/session/start`, {
      method: 'POST',
      headers: this.authHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        patient_id: request.patient_id,
        patient_name: request.patient_name,
        encounter_type: request.encounter_type,
        advance_mode: request.advance_mode
      })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to start session: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Get current session status with all transcript segments and proposals
   */
  async getSessionStatus(sessionId: string): Promise<SessionStatusResponse> {
    const response = await fetch(`${this.gatewayUrl}/session/${sessionId}/status`, {
      headers: this.authHeaders()
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to get session status: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * End an active session
   */
  async endSession(sessionId: string): Promise<void> {
    const response = await fetch(`${this.gatewayUrl}/session/${sessionId}/end`, {
      method: 'POST',
      headers: this.authHeaders()
    })

    if (!response.ok && response.status !== 404) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to end session: ${response.statusText}`)
    }
  }

  /**
   * Submit transcript text directly (for testing without audio)
   */
  async submitTranscript(sessionId: string, text: string): Promise<{ proposals: AgentProposal[] }> {
    const response = await fetch(`${this.gatewayUrl}/transcript/submit`, {
      method: 'POST',
      headers: this.authHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        session_id: sessionId,
        text: text
      })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to submit transcript: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Submit pre-aligned transcript segments (manual / test-entry path).
   *
   * Distinct from submitTranscript({text}) — this sends the segment array the
   * gateway's /transcript/submit endpoint expects and returns the full
   * response (proposals_triggered, rasa_errors, flow_context).
   */
  async submitSegments(
    sessionId: string,
    segments: Array<{ speaker: string; text: string; start: number; end: number }>
  ): Promise<any> {
    const response = await fetch(`${this.gatewayUrl}/transcript/submit`, {
      method: 'POST',
      headers: this.authHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        session_id: sessionId,
        segments
      })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to submit segments: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Generate TTS audio for a test utterance and (optionally) run it back
   * through the Whisper pipeline. Returns the gateway's TTS payload.
   */
  async ttsSpeak(request: {
    session_id: string
    text: string
    speaker: string
    process_audio: boolean
  }): Promise<any> {
    const response = await fetch(`${this.gatewayUrl}/tts/speak`, {
      method: 'POST',
      headers: this.authHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(request)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to generate TTS: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Swap the navigator/patient speaker assignment for a session.
   * Returns the new swap state and any corrected last segment.
   */
  async swapSpeakers(sessionId: string): Promise<{
    speakers_swapped: boolean
    corrected_segment?: { new_speaker: string }
  }> {
    const response = await fetch(`${this.gatewayUrl}/session/${sessionId}/swap-speakers`, {
      method: 'POST',
      headers: this.authHeaders({
        'Content-Type': 'application/json'
      })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to swap speakers: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Enable/disable manual speaker mode for a session. The `enabled` flag is a
   * query param to match the gateway route signature.
   */
  async setManualSpeakerMode(sessionId: string, enabled: boolean): Promise<void> {
    const response = await fetch(
      `${this.gatewayUrl}/session/${sessionId}/manual-speaker-mode?enabled=${enabled}`,
      {
        method: 'POST',
        headers: this.authHeaders({
          'Content-Type': 'application/json'
        })
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to set manual speaker mode: HTTP ${response.status}`)
    }
  }

  /**
   * Get WebSocket URL for audio streaming
   */
  getWebSocketUrl(sessionId: string): string {
    const wsProtocol = this.gatewayUrl.startsWith('https') ? 'wss' : 'ws'
    const wsHost = this.gatewayUrl.replace(/^https?:\/\//, '')
    // WS clients can't set headers — the gateway authenticates via ?token=
    return `${wsProtocol}://${wsHost}/ws/audio/${sessionId}?token=${encodeURIComponent(getGatewayToken())}`
  }

  /**
   * Get SSE events URL for real-time updates
   */
  getEventsUrl(sessionId: string): string {
    // EventSource can't set headers — the gateway authenticates via ?token=
    return `${this.gatewayUrl}/events/${sessionId}?token=${encodeURIComponent(getGatewayToken())}`
  }

  /**
   * Connect to SSE events stream
   * Returns a cleanup function to close the connection
   */
  connectToEvents(
    sessionId: string,
    onTranscript: (segment: TranscriptSegment) => void,
    onProposal: (proposal: AgentProposal) => void,
    onError: (error: Error) => void,
    onCorrection?: (correction: TranscriptCorrection) => void,
    onFlowContext?: (context: FlowContext | null) => void,
    onFlowDismissed?: (data: { dismissed_flow: string; display_name: string; reason: string }) => void,
    onSlotCorrected?: (data: { slot_name: string; new_value: any; source: string }) => void,
    onFlowCompleted?: (data: FlowCompletedEvent) => void,
    onTaskProgress?: (data: TaskProgressEvent) => void,
    onEncounterComplete?: (data: EncounterFlowsCompleteEvent) => void,
    onFlowStopped?: (data: { flow_name: string; display_name: string }) => void,
    onAdhocFlowStarted?: (data: AdhocFlowStartedEvent) => void,
    onAdhocFlowCompleted?: (data: AdhocFlowCompletedEvent) => void,
    onAgentResponse?: (data: AgentResponseEvent) => void
  ): () => void {
    const eventsUrl = this.getEventsUrl(sessionId)
    const eventSource = new EventSource(eventsUrl)

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as SSEEvent

        switch (data.type) {
          case 'transcript':
            onTranscript(data.data as TranscriptSegment)
            break
          case 'proposal':
            onProposal(data.data as AgentProposal)
            break
          case 'transcript_correction':
            onCorrection?.(data.data as TranscriptCorrection)
            break
          case 'flow_context':
            onFlowContext?.(data.data as FlowContext | null)
            break
          case 'flow_dismissed':
            onFlowDismissed?.(data.data as { dismissed_flow: string; display_name: string; reason: string })
            break
          case 'slot_corrected':
            onSlotCorrected?.(data.data as { slot_name: string; new_value: any; source: string })
            break
          case 'flow_completed':
            onFlowCompleted?.(data.data as FlowCompletedEvent)
            break
          case 'task_progress':
            onTaskProgress?.(data.data as TaskProgressEvent)
            break
          case 'encounter_flows_complete':
            onEncounterComplete?.(data.data as EncounterFlowsCompleteEvent)
            break
          case 'flow_stopped':
            onFlowStopped?.(data.data as { flow_name: string; display_name: string })
            break
          case 'adhoc_flow_started':
          case 'unplanned_flow':
            onAdhocFlowStarted?.(data.data as AdhocFlowStartedEvent)
            break
          case 'adhoc_flow_completed':
            onAdhocFlowCompleted?.(data.data as AdhocFlowCompletedEvent)
            break
          case 'agent_response':
            onAgentResponse?.(data.data as AgentResponseEvent)
            break
          case 'error':
            onError(new Error((data.data as { error: string }).error))
            break
        }
      } catch (err) {
        console.error('Failed to parse SSE event:', err)
      }
    }

    eventSource.onerror = (event) => {
      // EventSource will auto-reconnect, but we log the error
      console.error('SSE connection error:', event)
      onError(new Error('Connection to events stream lost'))
    }

    // Return cleanup function
    return () => {
      eventSource.close()
    }
  }

  /**
   * Record a decision on a proposal (approve, reject, defer)
   */
  async decideProposal(
    sessionId: string,
    proposalId: string,
    decision: ProposalDecision
  ): Promise<{ status: string; proposal_id: string; decision: string }> {
    const response = await fetch(`${this.gatewayUrl}/proposals/decide`, {
      method: 'POST',
      headers: this.authHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        session_id: sessionId,
        proposal_id: proposalId,
        decision: decision
      })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to record decision: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Execute an approved UI action proposal against the Flask backend.
   * The proposal must be approved first via decideProposal().
   */
  async executeProposal(
    sessionId: string,
    proposalId: string
  ): Promise<{
    status: 'success' | 'failed'
    proposal_id: string
    action_type?: string
    response_status?: number
    error?: string
  }> {
    const response = await fetch(`${this.gatewayUrl}/proposals/execute`, {
      method: 'POST',
      headers: this.authHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        session_id: sessionId,
        proposal_id: proposalId
      })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to execute proposal: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Approve and execute a proposal in one step.
   * Convenience method that calls decideProposal then executeProposal.
   */
  async approveAndExecute(
    sessionId: string,
    proposalId: string
  ): Promise<{
    decision_status: string
    execution_status: 'success' | 'failed'
    error?: string
  }> {
    // First approve the proposal
    await this.decideProposal(sessionId, proposalId, 'approved')

    // Then execute it
    const result = await this.executeProposal(sessionId, proposalId)

    return {
      decision_status: 'approved',
      execution_status: result.status,
      error: result.error
    }
  }

  /**
   * Dismiss the currently active flow in the executor.
   * Clears active_flow and returns the session to its default state.
   *
   * @param reason - Why the flow was dismissed: "wrong_flow", "skip_for_now", "topic_changed"
   */
  async dismissFlow(sessionId: string, reason: string = 'unspecified'): Promise<{
    status: string
    dismissed_flow: string
    reason: string
  }> {
    const response = await fetch(`${this.gatewayUrl}/session/${sessionId}/dismiss-flow`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ reason })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to dismiss flow: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Trigger the next flow in the queue (hybrid/manual mode).
   * Called when user clicks "Start" on the next task in the task list.
   */
  async advanceFlow(sessionId: string, flowName?: string): Promise<{
    status: string
    flow_name: string
    current_task: FlowTask
    remaining_count: number
    completed_count: number
  }> {
    const response = await fetch(`${this.gatewayUrl}/session/${sessionId}/advance-flow`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(flowName ? { flow_name: flowName } : {})
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to advance flow: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Trigger an ad-hoc flow during an active session.
   * Creates a Task record (unless create_task=false) and starts the flow in the executor.
   */
  async triggerFlow(sessionId: string, request: FlowTriggerRequest): Promise<FlowTriggerResponse> {
    const response = await fetch(`${this.gatewayUrl}/session/${sessionId}/trigger-flow`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(request)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to trigger flow: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Fetch the ad-hoc flow registry for the flow picker UI.
   * Optionally filter by user_role and/or category.
   */
  async getAdhocFlowRegistry(userRole?: string, category?: string): Promise<AdhocFlowRegistryEntry[]> {
    const params = new URLSearchParams()
    if (userRole) params.set('user_role', userRole)
    if (category) params.set('category', category)
    const qs = params.toString()

    const response = await fetch(`${this.gatewayUrl}/adhoc-flow-registry${qs ? `?${qs}` : ''}`, {
      headers: this.authHeaders()
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to fetch flow registry: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Stop the currently running flow without completing the task.
   * Cancels the active flow and returns the task to "ready" state in the queue.
   */
  async stopFlow(sessionId: string): Promise<{
    status: string
    flow_name: string
    remaining_count: number
    completed_count: number
  }> {
    const response = await fetch(`${this.gatewayUrl}/session/${sessionId}/stop-flow`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to stop flow: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Manually mark a task as complete via the gateway.
   * Handles both planned flow tasks and ad-hoc flow tasks.
   * Cancels the active flow if running and updates session state.
   */
  async completeTask(sessionId: string, taskId: number, cancelFlow: boolean = true): Promise<{
    status: string
    task_id: number
    remaining_count: number
    completed_count: number
  }> {
    const response = await fetch(`${this.gatewayUrl}/session/${sessionId}/complete-task`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ task_id: taskId, cancel_flow: cancelFlow })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to complete task: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Correct an LLM-filled slot value in the executor's active flow.
   * Sends the corrected value to the executor before proposal execution.
   */
  async correctSlot(sessionId: string, slotName: string, newValue: any): Promise<{
    status: string
    slot_name: string
    new_value: any
  }> {
    const response = await fetch(`${this.gatewayUrl}/session/${sessionId}/correct-slot`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ slot_name: slotName, new_value: newValue })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to correct slot: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Get full session state from gateway (transcript + proposals + metadata).
   * Used to rehydrate UI after page refresh.
   */
  async getFullSession(sessionId: string): Promise<{
    session_id: string
    patient_id: string | null
    start_time: string
    end_time: string | null
    transcript_segments: TranscriptSegment[]
    proposals: AgentProposal[]
    encounter_type: string | null
    speakers_swapped: boolean
    manual_speaker_mode: boolean
  }> {
    const response = await fetch(`${this.gatewayUrl}/session/${sessionId}`, {
      headers: this.authHeaders()
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to get session: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Get post-encounter summary with proposals, decisions, and stats.
   */
  async getSessionSummary(sessionId: string): Promise<SessionSummary> {
    const response = await fetch(`${this.gatewayUrl}/session/${sessionId}/summary`, {
      headers: this.authHeaders()
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to get session summary: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Grade a proposal from a completed encounter.
   */
  async gradeProposal(
    sessionId: string,
    proposalId: string,
    flowCorrect: boolean | null,
    captureCorrect: boolean | null,
    notes?: string,
    phrasingQuality?: number | null,
    phrasingEdit?: string | null,
  ): Promise<{ status: string; proposal_id: string }> {
    const body: Record<string, any> = {
      proposal_id: proposalId,
      flow_correct: flowCorrect,
      capture_correct: captureCorrect,
      notes: notes || null,
    }
    if (phrasingQuality != null) body.phrasing_quality = phrasingQuality
    if (phrasingEdit != null) body.phrasing_edit = phrasingEdit

    const response = await fetch(`${this.gatewayUrl}/session/${sessionId}/grade`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to grade proposal: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Grade a single transcript segment from a completed encounter (DM-7768).
   * segmentIndex is the server-side index into the session's
   * transcript_segments — grade against segments fetched from /summary,
   * never against locally assembled Redux state.
   */
  async gradeSegment(
    sessionId: string,
    segmentIndex: number,
    captureCorrect: boolean | null,
    correctedValue?: string | null,
    notes?: string | null,
  ): Promise<{ status: string; segment_index: number }> {
    const response = await fetch(
      `${this.gatewayUrl}/session/${sessionId}/segments/${segmentIndex}/grade`,
      {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          capture_correct: captureCorrect,
          corrected_value: correctedValue || null,
          notes: notes || null,
        })
      }
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to grade segment: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Grade the overall encounter quality.
   */
  async gradeEncounter(
    sessionId: string,
    overallQuality: number | null,
    notes?: string | null,
  ): Promise<{ status: string }> {
    const response = await fetch(`${this.gatewayUrl}/session/${sessionId}/encounter-grade`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        overall_quality: overallQuality,
        notes: notes || null,
      })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(error.detail || `Failed to grade encounter: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Download full session data as a JSON file for audit/learning.
   */
  async downloadSession(sessionId: string, patientName?: string): Promise<void> {
    const data = await this.getFullSession(sessionId)

    const filename = [
      'agent-eval',
      data.encounter_type || 'session',
      new Date().toISOString().slice(0, 10),
      sessionId.slice(0, 8)
    ].join('_') + '.json'

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  /**
   * Export all agent eval sessions as CSV (no patient identifiers).
   * Hits the Flask backend, not the gateway.
   */
  async exportSessions(startDate?: string, endDate?: string): Promise<Blob> {
    const apiEndpoint = environment.webservices.endpoint
    const params = new URLSearchParams()
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)

    const response = await axios.get(
      `${apiEndpoint}/agent-eval/sessions/export?${params.toString()}`,
      { responseType: 'blob' }
    )
    return response.data
  }

  /**
   * Health check for gateway
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.gatewayUrl}/health`)
      return response.ok
    } catch {
      return false
    }
  }
}

// Export singleton instance
export const agentEvalService = new AgentEvalService()
export default AgentEvalService
